import MeetingService from '../../services/MeetingService';
import SectionService from '../../services/SectionService';
import UploadService from '../../services/UploadService';
import extractVideoDuration from './extractVideoDuration';

const MAX_CONCURRENT = 3;
const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5 MB

const ERROR_MESSAGES = {
  create: 'Failed to create video',
  upload: 'Upload failed',
  attach: 'Failed to attach video',
  publish: 'Processing failed',
  unknown: 'Something went wrong',
};

/**
 * Processes a single row through the full import pipeline:
 *   createMeeting → upload to S3 → placeholder + completeSection → (optional) publish
 *
 * For publishNow rows, the slot is released after the publish PATCH returns
 * (row enters 'processing'). Socket events drive the final done/failed transition.
 *
 * For non-publish rows, the slot is released when the row reaches 'draft'.
 */
async function processRow(row, onUpdate) {
  const { rowId, file, title, folderId, publishNow } = row;

  // Step 1: Create meeting
  let meetingId;
  try {
    onUpdate(rowId, { status: 'creating' });
    const meeting = await MeetingService.createMeeting({
      title,
      folderId,
      type: 'my videos',
    });
    meetingId = meeting.id;
    onUpdate(rowId, { meetingId });
  } catch {
    onUpdate(rowId, { status: 'failed', errorMessage: ERROR_MESSAGES.create });
    return;
  }

  // Step 2: Create placeholder section
  let sectionId;
  try {
    const section = await SectionService.createPlaceholderSection({
      meetingId,
      title,
      type: 'video',
    });
    sectionId = section.id;
    onUpdate(rowId, { sectionId });
  } catch {
    onUpdate(rowId, { status: 'failed', errorMessage: ERROR_MESSAGES.attach });
    return;
  }

  // Step 3: Upload to S3
  let s3FileKey;
  try {
    onUpdate(rowId, { status: 'uploading', progress: 0 });

    const progressCallback = (progress) => {
      onUpdate(rowId, { progress: Math.round(progress) });
    };

    if (file.size < MULTIPART_THRESHOLD) {
      s3FileKey = await UploadService.uploadVideoToS3(file, progressCallback);
    } else {
      s3FileKey = await UploadService.uploadVideoToS3InParts(
        file,
        progressCallback
      );
    }
  } catch {
    onUpdate(rowId, { status: 'failed', errorMessage: ERROR_MESSAGES.upload });
    return;
  }

  // Step 4: Complete section (attach file)
  try {
    onUpdate(rowId, { status: 'attaching' });

    const duration = await extractVideoDuration(file);
    const fileType = 'video';

    await SectionService.completeSection({
      sectionId,
      s3FileKey,
      duration,
      fileType,
    });
  } catch {
    onUpdate(rowId, { status: 'failed', errorMessage: ERROR_MESSAGES.attach });
    return;
  }

  // Step 5: Publish (conditional)
  if (publishNow) {
    try {
      await MeetingService.updateMeeting({
        meetingId,
        status: 'published',
        shouldCreateNewVersion: true,
      });
      // Slot released here — row enters 'processing', socket events drive done/failed
      onUpdate(rowId, { status: 'processing', progress: 100 });
    } catch {
      onUpdate(rowId, {
        status: 'failed',
        errorMessage: ERROR_MESSAGES.publish,
      });
    }
  } else {
    onUpdate(rowId, { status: 'draft', progress: 100 });
  }
}

/**
 * Runs the import queue with a concurrency cap.
 * Processes importable rows in table order, up to MAX_CONCURRENT at a time.
 */
export default function runImportQueue(rows, onUpdate) {
  const importable = rows.filter(
    (r) => r.status === 'ready' && !r.validationError
  );

  if (importable.length === 0) return Promise.resolve();

  const queue = { nextIndex: 0, activeCount: 0, resolve: null };
  const done = new Promise((resolve) => {
    queue.resolve = resolve;
  });

  function startNext() {
    while (queue.activeCount < MAX_CONCURRENT && queue.nextIndex < importable.length) {
      const row = importable[queue.nextIndex];
      queue.nextIndex += 1;
      queue.activeCount += 1;
      processRow(row, onUpdate).finally(() => {
        queue.activeCount -= 1;
        if (queue.nextIndex < importable.length) {
          startNext();
        } else if (queue.activeCount === 0) {
          queue.resolve();
        }
      });
    }
  }

  startNext();
  return done;
}
